// src/components/layout/Header.tsx
import { Menu, LogOut, ArrowLeftToLine, ArrowRightToLine, UserCircle, XCircle } from 'lucide-react'; // Added UserCircle, Settings
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button'; // Adjust path if needed
import { SidebarState } from '../../pages/DashboardPage'; // Adjust path to where SidebarState is defined
import { useAuth } from '../../contexts/AuthContext';
import { signIn, stopImpersonation } from '../../services/authService'; // Added signIn import
import { useState } from 'react'; // Added useState
import { useNavigate } from 'react-router-dom'; // Added useNavigate
import { LanguageSelector } from './LanguageSelector';

interface HeaderProps {
  onToggleSidebarMobile: () => void;
  onToggleSidebarDesktop: () => void;
  sidebarState: SidebarState;
  isMobile: boolean;
}

export default function Header({ 
  onToggleSidebarMobile, 
  onToggleSidebarDesktop, 
  sidebarState, 
  isMobile,
}: HeaderProps) {
  const { t } = useTranslation();
  const { user, logout, isAuthenticated, isLoading } = useAuth();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false); // Added
  const [isStoppingImpersonation, setIsStoppingImpersonation] = useState(false);
  const navigate = useNavigate(); // Added

  const handleLogout = async () => {
    await logout(); 
    // setIsProfileDropdownOpen(false); // Close dropdown on logout
    signIn();
  };

  const handleStopImpersonation = async () => {
    const confirmStop = window.confirm(t('confirmStopImpersonation'));
    if (!confirmStop) return;

    setIsStoppingImpersonation(true);
    try {
      await stopImpersonation();
      toast.success(t('impersonationStopped'));
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      toast.error(t('errorStoppingImpersonation'));
    } finally {
      setIsStoppingImpersonation(false);
    }
  };

  // const handleLogin = () => {
  //   signIn();
  // };

  return (
    <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 md:px-6">
      <div className="flex items-center">
        {isMobile ? (
          <Button variant="ghost" size="icon" onClick={onToggleSidebarMobile} className="md:hidden">
            <Menu className="h-6 w-6 text-gray-600" />
            <span className="sr-only">{t('toggleMenu')}</span>
          </Button>
        ) : (
          <Button variant="ghost" size="icon" onClick={onToggleSidebarDesktop} title={sidebarState === 'open' ? t('collapseSidebar') : t('expandSidebar')}>
            {sidebarState === 'open' ? <ArrowLeftToLine className="h-6 w-6 text-gray-600" /> : <ArrowRightToLine className="h-6 w-6 text-gray-600" />}
          </Button>
        )}
      </div>
      <div className="flex items-center gap-4">
        {!isLoading && (
          isAuthenticated && user ? ( // Ensure user object exists
            <>
              <LanguageSelector/>
              
              {/* Impersonation Stop Button */}
              {user.isImpersonating && (
                <button
                  onClick={handleStopImpersonation}
                  disabled={isStoppingImpersonation}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t('stopImpersonation')}
                >
                  <XCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">{isStoppingImpersonation ? t('stopping') : t('stopImpersonation')}</span>
                </button>
              )}

              <span className="text-sm text-gray-700 hidden sm:inline">
                {t('welcomeUser', { name: user.name || user.email || t('user') })} {/* Updated welcome message */}
              </span>
              <div className="relative">
                <Button variant="ghost" size="icon" onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}>
                  {/* Placeholder for user image or icon */}
                  <UserCircle className="h-6 w-6 text-gray-600" />
                </Button>
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <button
                      onClick={() => { navigate('/profile'); setIsProfileDropdownOpen(false); }}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <UserCircle className="mr-2 h-4 w-4" /> {t('profile')}
                    </button>
                    {/* Example for Settings - not part of this immediate task but good for structure */}
                    {/* <button
                      onClick={() => { navigate('/settings'); setIsProfileDropdownOpen(false); }}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Settings className="mr-2 h-4 w-4" /> Settings
                    </button> */}
                    <button
                      onClick={handleLogout} // handleLogout already closes dropdown
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOut className="mr-2 h-4 w-4" /> {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : null
        )}
      </div>
    </header>
  );
}

// ) : !isAuthenticated ? ( // Show login only if not authenticated
//             <Button variant="outline" size="sm" onClick={handleLogin}>
//               <LogIn className="mr-2 h-4 w-4" />
//               Login with External Provider
//             </Button>
//           ) : null